import { useMemo, useState } from "react";
import { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { FileDown, FileText } from "lucide-react";

const RANGES = [
  { value: "week", label: "1 Woche" },
  { value: "month", label: "1 Monat" },
  { value: "6months", label: "6 Monate" },
  { value: "year", label: "1 Jahr" },
  { value: "all", label: "Alles" },
];

const toKey = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export default function WorktimeExportDialog({ open, onOpenChange, kind, drivers, periodStart, periodEnd }) {
  const [all, setAll] = useState(true);
  const [sel, setSel] = useState(new Set());
  const [range, setRange] = useState("month");

  const driverOpts = useMemo(() => drivers.map((d) => ({ value: d.id, label: `${d.firstname} ${d.lastname}` })), [drivers]);

  const computeRange = () => {
    // Anchor: end of currently displayed period; go backwards.
    const end = periodEnd ? new Date(periodEnd + "T00:00:00") : new Date();
    let start;
    if (range === "all") {
      start = new Date("2000-01-01T00:00:00");
    } else {
      start = new Date(end);
      if (range === "week") start.setDate(start.getDate() - 6);
      else if (range === "month") { start.setMonth(start.getMonth() - 1); start.setDate(start.getDate() + 1); }
      else if (range === "6months") { start.setMonth(start.getMonth() - 6); start.setDate(start.getDate() + 1); }
      else if (range === "year") { start.setFullYear(start.getFullYear() - 1); start.setDate(start.getDate() + 1); }
    }
    return { start: toKey(start), end: toKey(end) };
  };

  const toggle = (val) => setSel((prev) => { const n = new Set(prev); n.has(val) ? n.delete(val) : n.add(val); return n; });

  const doExport = () => {
    const { start, end } = computeRange();
    const p = new URLSearchParams();
    p.set("start", start);
    p.set("end", end);
    if (!all) sel.forEach((v) => p.append("driver_ids", v));
    window.open(`${API}/exports/worktime-summary.${kind}?${p.toString()}`, "_blank");
    onOpenChange(false);
  };

  const Icon = kind === "pdf" ? FileText : FileDown;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="worktime-export-dialog">
        <DialogHeader>
          <DialogTitle>Arbeitszeit exportieren ({kind.toUpperCase()})</DialogTitle>
          <DialogDescription>Alle oder bestimmte Fahrer und ein Zeitraum (ab der aktuell angezeigten Periode rückwärts).</DialogDescription>
        </DialogHeader>

        <label className="flex items-center justify-between gap-3 rounded-md border border-border p-3 bg-muted/40">
          <span className="text-sm font-medium">Alle Fahrer exportieren</span>
          <Switch checked={all} onCheckedChange={setAll} data-testid="worktime-export-all-switch" />
        </label>

        <div className={`${all ? "opacity-40 pointer-events-none" : ""}`} data-testid="worktime-export-drivers">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Fahrer</div>
          <div className="max-h-40 overflow-y-auto rounded-md border border-border p-2 space-y-1.5">
            {driverOpts.map((o) => (
              <label key={o.value} className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={sel.has(o.value)} onCheckedChange={() => toggle(o.value)} data-testid={`worktime-export-driver-${o.value}`} />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Zeitraum</div>
          <RadioGroup value={range} onValueChange={setRange} className="grid grid-cols-2 gap-2" data-testid="worktime-export-range">
            {RANGES.map((r) => (
              <label key={r.value} className="flex items-center gap-2 text-sm cursor-pointer rounded-md border border-border px-3 py-2">
                <RadioGroupItem value={r.value} data-testid={`worktime-export-range-${r.value}`} />
                <span>{r.label}</span>
              </label>
            ))}
          </RadioGroup>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={doExport} data-testid="worktime-export-confirm"><Icon className="h-4 w-4 mr-1" /> Exportieren</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
